"use client"

import { ampli } from "@/ampli"
import { toast } from "@/components/Toast"
import { Button, Text } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import {
  ApplyToJobDocument,
  JobApplicationProjectPreviewFragmentDoc,
  JobPostingPayRate,
  JobPostingPayType,
} from "@/gql/graphql"
import type { NovaSession } from "@/types"
import { revalidateTagsHelper } from "@/utils/revalidateTagHelper"
import { DocumentNode, useMutation } from "@apollo/client"
import { captureException } from "@sentry/nextjs"
import { cache, Dispatch, FC, SetStateAction, useCallback, useState } from "react"
import { JobsDrawerFooterSkeleton } from "../JobsDrawerFooterSkeleton"
import { decodeGraphqlNodeId } from "@/tracking"
import { getFragmentData } from "@/gql-clients/experimental/getFragmentData"
import { ControlledModal } from "@/components"
import { FormField, ProposedRateForm } from "./ApplyToJobContent"

const ApplyToJobFooterFragment = graphql(`
  fragment ApplyToJobFooter on JobPosting {
    __typename
    id
    payString
    payType
    payRate
    employmentTypeDisplay
    activeInteractionForCreative(creativeId: $creativeId) {
      __typename
      ... on Node {
        id
      }
    }
  }
`)

graphql(`
  mutation ApplyToJob($input: ApplyToJobInput!, $creativeId: ID!) {
    applyToJob(input: $input) {
      errors {
        field
        messages
      }
      jobApplication {
        id
        status
        createdAt
        jobPosting {
          id
          status
          applicationCount
          activeInteractionForCreative(creativeId: $creativeId) {
            ... on JobApplication {
              id
              __typename
              applicationStatus: status
              invitation {
                id
                status
                acceptedAt
              }
              chat {
                id
              }
            }
            ... on JobInvitation {
              id
              __typename
              invitationStatus: status
              createdAt
              message
              acceptedAt
            }
          }
        }
      }
    }
  }
`)

interface Props {
  jobPosting: FragmentType<typeof ApplyToJobFooterFragment> | undefined | null
  onClose?: () => void
  onSubmit?: () => void
  serverSession: NovaSession
  coverLetter: FormField
  proposedRate: ProposedRateForm
  loading: boolean
  submitApplicationRefetchQueries?: DocumentNode[]
  entryPoint: "job-board" | "invitation" | "job-page"
  creativeWorkAttachments: FragmentType<typeof JobApplicationProjectPreviewFragmentDoc>[]
  goToSelectWork: () => void
  onApplySuccess?: () => void
}

export const ApplyToJobFooter: FC<Props> = ({
  onClose,
  onSubmit,
  submitApplicationRefetchQueries,
  serverSession,
  coverLetter,
  proposedRate,
  loading,
  entryPoint,
  creativeWorkAttachments,
  goToSelectWork,
  onApplySuccess,
  ...props
}) => {
  const jobPosting = useFragment(ApplyToJobFooterFragment, props.jobPosting)
  const [submitApplication, { loading: loadingSubmitApplication }] = useMutation(ApplyToJobDocument)
  const [showNoProjectsWarningModal, setShowNoProjectsWarningModal] = useState(false)

  const creativeId = serverSession?.user?.creative?.id
  const jobPostingId = jobPosting?.id

  const handleSubmitApplication = useCallback(
    async (bypassProjectsWarning = false) => {
      if (!jobPostingId || !creativeId) {
        toast.error("Error submitting application")
        return
      }

      if (!coverLetter.value) {
        coverLetter.setError("Please include a note")
        window.document.getElementById("add-note-section")?.scrollIntoView({ behavior: "smooth" })
        return
      } else {
        coverLetter.setError(undefined)
      }

      if (!bypassProjectsWarning && creativeWorkAttachments.length === 0) {
        setShowNoProjectsWarningModal(true)
        // ampli.
        return
      }

      try {
        const payType = proposedRate.payType
          ? proposedRate.payType
          : jobPosting?.payType
            ? jobPosting.payType
            : JobPostingPayType.FixedFee
        const res = await submitApplication({
          variables: {
            input: {
              jobPosting: jobPostingId,
              creative: creativeId,
              invitation: jobPosting?.activeInteractionForCreative?.id || undefined,
              coverLetter: coverLetter.value || undefined,
              creativeWorkAttachments: creativeWorkAttachments.map(cw => ({
                portfolioProject: getFragmentData(JobApplicationProjectPreviewFragmentDoc, cw).id,
              })),
              ...(proposedRate.value && {
                proposedPay: { currency: "USD", amount: proposedRate.value },
                proposedPayType: payType,
                proposedPayRateFrequency:
                  payType === JobPostingPayType.FixedFee
                    ? undefined
                    : proposedRate.payRateFrequency
                      ? proposedRate.payRateFrequency
                      : jobPosting?.payRate
                        ? jobPosting.payRate
                        : JobPostingPayRate.Hour,
              }),
            },
            creativeId: creativeId,
          },
          awaitRefetchQueries: true,
          refetchQueries: submitApplicationRefetchQueries,
        })
        if (res.data?.applyToJob?.errors && res.data?.applyToJob?.errors.length > 0) {
          throw new Error("Error submitting application")
        } else {
          ampli.applyToJobComplete({
            job_id: decodeGraphqlNodeId(jobPostingId).numericalId || "",
            job_type: jobPosting?.__typename === "ClientJobPosting" ? "client" : "creative",
          })

          toast.success("Application submitted")
          revalidateTagsHelper(["jobs-id-server-authenticated-query"])
          onApplySuccess?.()
          onClose?.()
        }
      } catch (error) {
        captureException(error)
        console.error(error)
      }
    },
    [
      jobPostingId,
      creativeId,
      coverLetter,
      creativeWorkAttachments,
      submitApplication,
      jobPosting?.activeInteractionForCreative?.id,
      jobPosting?.__typename,
      submitApplicationRefetchQueries,
      onApplySuccess,
      onClose,
    ]
  )

  if (loading) {
    return <JobsDrawerFooterSkeleton />
  }

  return (
    <div className="flex h-full w-full flex-row items-center justify-between">
      <div className="flex flex-col gap-y-1">
        <Text variant="h5">{jobPosting?.payString}</Text>
        <Text variant="label" className="text-fg-2" maxLines={1}>
          {jobPosting?.employmentTypeDisplay}
        </Text>
      </div>
      <div className="flex flex-row items-center gap-2">
        <Button
          size="large"
          className="hidden md:block"
          onClick={onClose}
          variant="transparent"
          disabled={loadingSubmitApplication}>
          Cancel
        </Button>
        <Button className="md:hidden" onClick={onClose} variant="transparent" disabled={loadingSubmitApplication}>
          Cancel
        </Button>
        <Button
          size="large"
          className="hidden md:block"
          onClick={() => handleSubmitApplication(false)}
          loading={loadingSubmitApplication}>
          Submit application
        </Button>
        <Button className="md:hidden" onClick={() => handleSubmitApplication(false)} loading={loadingSubmitApplication}>
          Submit
        </Button>
      </div>
      <ControlledModal
        zIndex={102}
        fullScreenOnMobile={false}
        header={{
          fixed: true,
          variant: "warning",
          title: "Are you sure?",
          subtitle:
            "You haven’t chosen any of your projects to be included in your application. Including projects increases your chances of getting contacted and hired.",
        }}
        open={showNoProjectsWarningModal}
        onOpenChange={(open: boolean) => {
          if (!open) {
            ampli.noAttachedWorkWarning({
              action: "cancel",
            })
          }
          setShowNoProjectsWarningModal(open)
        }}
        onClose={() => {
          ampli.noAttachedWorkWarning({
            action: "cancel",
          })
          setShowNoProjectsWarningModal(false)
        }}
        footer={{
          fixed: true,
          primaryButton: {
            children: "Select projects",
            onClick: () => {
              ampli.noAttachedWorkWarning({
                action: "select-projects",
              })
              goToSelectWork()
            },
          },
          secondaryButton: {
            children: "Apply anyway",
            onClick: () => {
              handleSubmitApplication(true)
              ampli.noAttachedWorkWarning({
                action: "apply-anyway",
              })
            },
          },
          tertiaryButton: {
            children: "Cancel",
            onClick: () => {
              setShowNoProjectsWarningModal(false)
              ampli.noAttachedWorkWarning({
                action: "cancel",
              })
            },
            className: "hidden md:block",
          },
        }}
      />
    </div>
  )
}
